import * as React from "react";
import {
  AlertTriangle,
  FolderOpen,
  MessageSquare,
  Settings2,
  Share2,
  Trash2,
  Users,
  Workflow,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { getIntlLocale } from "@/utils/language";

type DependencyItem = {
  id: string | number;
  name?: string | null;
  description?: string | null;
  updated_at?: string | null;
};

type DependencyGroup = {
  count: number;
  items?: DependencyItem[];
};

export type ProjectDependencies = {
  has_dependencies: boolean;
  total_count: number;
  [key: string]: boolean | number | DependencyGroup | undefined;
};

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

const isDependencyItem = (value: unknown): value is DependencyItem => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const { id } = value as { id?: unknown };
  return (
    (typeof id === "string" && id.trim().length > 0) ||
    (typeof id === "number" && Number.isFinite(id))
  );
};

export const isValidProjectDependencies = (
  value: unknown,
): value is ProjectDependencies => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  const response = value as Record<string, unknown>;
  if (
    typeof response.has_dependencies !== "boolean" ||
    !isNonNegativeInteger(response.total_count)
  ) {
    return false;
  }

  let dependencyCount = 0;
  for (const [key, groupValue] of Object.entries(response)) {
    if (key === "has_dependencies" || key === "total_count") continue;
    if (groupValue === null || groupValue === undefined) continue;
    if (
      typeof groupValue !== "object" ||
      Array.isArray(groupValue) ||
      !isNonNegativeInteger((groupValue as { count?: unknown }).count)
    ) {
      return false;
    }

    const group = groupValue as { count: number; items?: unknown };
    if (group.count > 0) {
      if (
        !Array.isArray(group.items) ||
        group.items.length !== group.count ||
        !group.items.every(isDependencyItem)
      ) {
        return false;
      }
    } else if (
      group.items !== undefined &&
      (!Array.isArray(group.items) || group.items.length !== 0)
    ) {
      return false;
    }

    dependencyCount += group.count;
  }

  return response.has_dependencies
    ? response.total_count > 0 && dependencyCount === response.total_count
    : response.total_count === 0 && dependencyCount === 0;
};

type ProjectDeleteDialogProps = {
  open: boolean;
  projectName?: string;
  dependencies: ProjectDependencies | null;
  dependenciesLoading: boolean;
  dependencyError: string;
  mutationError: string;
  forceDelete: boolean;
  pending: boolean;
  returnFocusRef: React.RefObject<HTMLElement | null>;
  fallbackFocusElement?: HTMLElement | null;
  restoreFocusRef: React.RefObject<boolean>;
  onCancel: () => void;
  onRetry: () => void;
  onForceDeleteChange: (checked: boolean) => void;
  onConfirm: () => void;
};

const dependencyIcons: Record<string, React.ReactNode> = {
  collections: <FolderOpen aria-hidden="true" />,
  flows: <Workflow aria-hidden="true" />,
  conversations: <MessageSquare aria-hidden="true" />,
  llm_configs: <Settings2 aria-hidden="true" />,
  shared_users: <Users aria-hidden="true" />,
};

const ProjectDeleteDialog = ({
  open,
  projectName,
  dependencies,
  dependenciesLoading,
  dependencyError,
  mutationError,
  forceDelete,
  pending,
  returnFocusRef,
  fallbackFocusElement,
  restoreFocusRef,
  onCancel,
  onRetry,
  onForceDeleteChange,
  onConfirm,
}: ProjectDeleteDialogProps) => {
  const { t, i18n } = useTranslation();
  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);
  const dependencyTypes = React.useMemo(
    () =>
      dependencies
        ? Object.entries(dependencies).filter(
            (entry): entry is [string, DependencyGroup] => {
              const value = entry[1];
              return (
                typeof value === "object" &&
                value !== null &&
                typeof value.count === "number" &&
                value.count > 0
              );
            },
          )
        : [],
    [dependencies],
  );
  const [selectedType, setSelectedType] = React.useState("");
  const activeType = dependencyTypes.some(([key]) => key === selectedType)
    ? selectedType
    : (dependencyTypes[0]?.[0] ?? "");

  const requestClose = () => {
    if (!pending) onCancel();
  };

  const canRestoreFocus = (element: HTMLElement | null | undefined) =>
    Boolean(
      element?.isConnected &&
        !("disabled" in element && element.disabled) &&
        element.getAttribute("aria-disabled") !== "true",
    );

  const preventPendingDismissal = (event: Event) => {
    if (pending) event.preventDefault();
  };

  const formatLabel = (key: string) => {
    const labels: Record<string, string> = {
      collections: t("projects.collections"),
      flows: t("projects.flows"),
      llm_configs: t("projects.llmConfigurations"),
      conversations: t("projects.conversations"),
      shared_users: t("projects.sharedUsers"),
    };
    return labels[key] ?? key;
  };

  const formatDate = (isoString?: string | null) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(getIntlLocale(i18n.language || "en"), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (!dependencyTypes.length) return;
    const isRTL = i18n.dir() === "rtl";
    let nextIndex: number | null = null;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = dependencyTypes.length - 1;
    if (event.key === "ArrowRight") {
      nextIndex =
        (index + (isRTL ? -1 : 1) + dependencyTypes.length) %
        dependencyTypes.length;
    }
    if (event.key === "ArrowLeft") {
      nextIndex =
        (index + (isRTL ? 1 : -1) + dependencyTypes.length) %
        dependencyTypes.length;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const [nextType] = dependencyTypes[nextIndex];
    setSelectedType(nextType);
    const tabList = event.currentTarget.parentElement;
    const tabs = tabList?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabs?.[nextIndex]?.focus();
  };

  const canDelete =
    Boolean(dependencies) &&
    !dependenciesLoading &&
    !dependencyError &&
    !pending &&
    (!dependencies?.has_dependencies || forceDelete);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) requestClose();
      }}
    >
      <DialogContent
        data-testid="project-delete-dialog"
        dir={i18n.dir()}
        showCloseButton={false}
        className="max-h-[min(80dvh,48rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-3xl"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancelButtonRef.current?.focus({ preventScroll: true });
        }}
        onCloseAutoFocus={(event) => {
          if (!restoreFocusRef.current) return;
          const target = canRestoreFocus(returnFocusRef.current)
            ? returnFocusRef.current
            : canRestoreFocus(fallbackFocusElement)
              ? fallbackFocusElement
              : null;
          if (!target) return;
          event.preventDefault();
          target.focus({ preventScroll: true });
        }}
        onEscapeKeyDown={preventPendingDismissal}
        onPointerDownOutside={preventPendingDismissal}
        onInteractOutside={preventPendingDismissal}
      >
        <DialogHeader className="border-b border-border px-6 py-5 text-start">
          <div className="flex items-center gap-2">
            <AlertTriangle
              className="size-5 text-destructive"
              aria-hidden="true"
            />
            <DialogTitle>{t("projects.deleteProjectTitle")}</DialogTitle>
          </div>
          <DialogDescription>
            {t("projects.deleteProjectConfirm", { name: projectName })}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto px-6 py-5">
          {dependenciesLoading ? (
            <div
              className="flex min-h-24 items-center justify-center gap-3 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              <Spinner className="size-6" aria-hidden="true" />
              <span>{t("projects.checkingDependencies")}</span>
            </div>
          ) : dependencyError ? (
            <div
              className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
              role="alert"
            >
              <p>{dependencyError}</p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={onRetry}
              >
                {t("common.retry")}
              </Button>
            </div>
          ) : dependencies ? (
            <div className="space-y-5">
              {mutationError ? (
                <div
                  className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
                  role="alert"
                >
                  {mutationError}
                </div>
              ) : null}
              {dependencies.has_dependencies && dependencyTypes.length ? (
                <>
                  <div
                    className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
                    role="status"
                  >
                    {t("projects.hasDependencies", {
                      count: dependencies.total_count,
                    })}
                  </div>

                  <Tabs value={activeType} onValueChange={setSelectedType}>
                    <TabsList
                      className="max-w-full justify-start overflow-x-auto"
                      dir={i18n.dir()}
                    >
                      {dependencyTypes.map(([key, value], index) => (
                        <TabsTrigger
                          key={key}
                          id={`project-dependency-tab-${key}`}
                          value={key}
                          tabIndex={activeType === key ? 0 : -1}
                          aria-controls={`project-dependency-panel-${key}`}
                          onKeyDown={(event) => handleTabKeyDown(event, index)}
                          className="shrink-0"
                        >
                          {dependencyIcons[key] ?? (
                            <Share2 aria-hidden="true" />
                          )}
                          <span>{formatLabel(key)}</span>
                          <Badge variant="muted">{value.count}</Badge>
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {dependencyTypes.map(([key, value]) => (
                      <TabsContent
                        key={key}
                        id={`project-dependency-panel-${key}`}
                        value={key}
                        aria-labelledby={`project-dependency-tab-${key}`}
                        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                      >
                        {(value.items ?? []).map((item) => (
                          <Card key={item.id} className="min-w-0">
                            <CardContent className="space-y-1 p-4">
                              {item.name ? (
                                <p className="truncate text-sm font-semibold text-foreground">
                                  {item.name}
                                </p>
                              ) : null}
                              {item.description ? (
                                <p className="line-clamp-3 text-sm text-muted-foreground">
                                  {item.description}
                                </p>
                              ) : null}
                              {item.updated_at ? (
                                <p className="text-xs text-muted-foreground">
                                  {t("projects.lastEdited")}: {" "}
                                  {formatDate(item.updated_at)}
                                </p>
                              ) : null}
                            </CardContent>
                          </Card>
                        ))}
                      </TabsContent>
                    ))}
                  </Tabs>

                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-destructive/40 p-4 text-sm text-destructive">
                    <input
                      type="checkbox"
                      checked={forceDelete}
                      onChange={(event) =>
                        onForceDeleteChange(event.currentTarget.checked)
                      }
                      className="mt-0.5 size-4 shrink-0 accent-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      disabled={pending}
                    />
                    <span>
                      {t("projects.understandDeleteWarning", {
                        count: dependencies.total_count,
                      })}
                    </span>
                  </label>
                </>
              ) : (
                <div
                  className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-foreground"
                  role="status"
                >
                  {t("projects.noDependencies")}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter className="border-t border-border px-6 py-4">
          <Button
            ref={cancelButtonRef}
            type="button"
            variant="outline"
            onClick={requestClose}
            disabled={pending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={!canDelete}
          >
            {pending ? (
              <Spinner className="text-current" aria-hidden="true" />
            ) : (
              <Trash2 aria-hidden="true" />
            )}
            {pending
              ? t("projects.deleting")
              : dependencies?.has_dependencies
                ? t("projects.deleteProjectAndDeps")
                : t("projects.deleteProject")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDeleteDialog;
